import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Coins, Receipt, IndianRupee, HandCoins, TrendingUp, Scale } from "lucide-react";
import dayjs from "dayjs";
import toastr from "toastr";

import useDocumentTitle from "../../hooks/useDocumentTitle";
import ShimmerLoader from "../../hooks/ShimmerLoader";
import Pagination from "../../hooks/Pagination";
import downloadBlob from "../../utils/downloadBlob";
import { exportCommission } from "../../services/api";
import { fetchCommissionReport, fetchCommissionLedger } from "../../store/reportSlices/performanceReportSlice";
import { StatCard, ReportHeader, ErrorBanner, FilterActions, inputCls, num, inr, pct, fmtDateTime, customerName } from "./ReportUI";

const PER_PAGE = 25;
const buildPayload = (f) => ({
    from_date: f.fromDate || null,
    to_date: f.toDate || null,
    group_by: f.groupBy,
    settlement_status: f.settlement || null,
});
const defaultFilters = () => ({
    fromDate: dayjs().subtract(5, "month").startOf("month").format("YYYY-MM-DD"),
    toDate: dayjs().format("YYYY-MM-DD"),
    groupBy: "month",
    settlement: "",
});

export default function CommissionReport() {
    useDocumentTitle("Commission Collected vs Earned | Reports");
    const dispatch = useDispatch();
    const { data, loading, error } = useSelector((s) => s.performanceReport.commission);
    const ledger = useSelector((s) => s.performanceReport.commissionLedger);

    const [showFilters, setShowFilters] = useState(false);
    const [draft, setDraft] = useState(defaultFilters());
    const [applied, setApplied] = useState(defaultFilters());
    const [page, setPage] = useState(1);
    const [exporting, setExporting] = useState(false);

    const load = () => dispatch(fetchCommissionReport(buildPayload(applied)));
    useEffect(() => { load(); setPage(1); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dispatch, applied]);
    useEffect(() => {
        dispatch(fetchCommissionLedger({ perPage: PER_PAGE, page, filters: buildPayload(applied) }));
    }, [dispatch, page, applied]);

    const handleApply = (e) => {
        e.preventDefault();
        toastr.options = { closeButton: true, progressBar: true, timeOut: 3000, positionClass: "toast-top-center" };
        if (draft.fromDate && draft.toDate && draft.fromDate > draft.toDate) return toastr.error("From date cannot be after To date");
        if (draft.groupBy === "day" && (!draft.fromDate || !draft.toDate || dayjs(draft.toDate).diff(dayjs(draft.fromDate), "day") > 92))
            return toastr.error("Daily view needs a date range of up to 92 days");
        setApplied({ ...draft });
    };
    const handleReset = () => { const f = defaultFilters(); setDraft(f); setApplied(f); };

    const handleExport = async () => {
        setExporting(true);
        try { downloadBlob(await exportCommission(buildPayload(applied)), "commission-collected-vs-earned.csv"); }
        catch { toastr.error("Unable to export. Please try again."); }
        finally { setExporting(false); }
    };

    const s = data?.summary || { transactions: 0, order_value: 0, collected: 0, goerone_discount: 0, earned: 0, earned_pct: null, collected_settled: 0, collected_pending: 0 };
    const series = data?.series || [];
    const maxBar = Math.max(1, ...series.flatMap((p) => [p.collected, p.earned]));
    const rows = ledger.data?.data || [];
    const lastPage = ledger.data?.last_page || 1;
    const fmtPeriod = (p) => (p.length === 7 ? dayjs(`${p}-01`).format("MMM YYYY") : dayjs(p).format("DD MMM"));
    const diffCls = (v) => (v > 0 ? "text-amber-600" : v < 0 ? "text-rose-600" : "text-gray-500"); // negative earned shows red

    return (
        <div className="w-full space-y-4">
            <ReportHeader icon={Coins} title="Commission: Collected vs Earned"
                subtitle="Completed redemptions only · Actual commission = Fixed commission − (Customer discount − Store discount)"
                activeFilters={(applied.settlement ? 1 : 0) + (applied.groupBy !== "month" ? 1 : 0)}
                showFilters={showFilters} onToggleFilters={() => setShowFilters((v) => !v)}
                onExport={handleExport} exporting={exporting} exportDisabled={loading || !s.transactions} />

            {showFilters && (
                <form onSubmit={handleApply} className="bg-white rounded-xl shadow p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">From date</label>
                            <input type="date" value={draft.fromDate} onChange={(e) => setDraft({ ...draft, fromDate: e.target.value })} className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">To date</label>
                            <input type="date" value={draft.toDate} min={draft.fromDate || undefined} onChange={(e) => setDraft({ ...draft, toDate: e.target.value })} className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Group by</label>
                            <select value={draft.groupBy} onChange={(e) => setDraft({ ...draft, groupBy: e.target.value })} className={inputCls}>
                                <option value="month">Month</option>
                                <option value="day">Day (max 92 days)</option>
                            </select>
                        </div>
                        <FilterActions onReset={handleReset} />
                        {/* <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Settlement</label>
                            <select value={draft.settlement} onChange={(e) => setDraft({ ...draft, settlement: e.target.value })} className={inputCls}>
                                <option value="">All</option>
                                <option value="settled">Settled</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div> */}
                    </div>
                    
                </form>
            )}

            <ErrorBanner message={error} onRetry={load} />

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard icon={Receipt} label="Transactions" value={num(s.transactions)} accent="blue" />
                <StatCard icon={IndianRupee} label="Order Value" value={inr(s.order_value)} accent="slate" />
                <StatCard icon={HandCoins} label="Commission Collected" value={inr(s.collected)}  accent="green" />
                {/* <StatCard icon={HandCoins} label="Commission Collected" value={inr(s.collected)} sub={`${inr(s.collected_settled)} settled · ${inr(s.collected_pending)} pending`} accent="green" /> */}
                <StatCard icon={Scale} label="Customer Discount by GoerOne" value={inr(s.goerone_discount)} accent="purple" />
                <StatCard icon={TrendingUp} label="Actual Commission Earned" value={inr(s.earned)} sub="Fixed commission − GoerOne discount" accent="amber" />
            </div>

            {/* Chart */}
            <div className="bg-white rounded-xl shadow p-5">
                <div className="flex items-center gap-4 mb-4">
                    <h4 className="font-semibold text-gray-800">Collected vs earned</h4>
                    <div className="ml-auto flex items-center gap-4 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500" />Collected</span>
                        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" />Earned</span>
                    </div>
                </div>
                {loading && !data ? <><ShimmerLoader /><ShimmerLoader /></> : series.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6">No completed transactions for the selected filters.</p>
                ) : (
                    <div className={`space-y-3 ${loading ? "opacity-60" : ""}`}>
                        {series.map((p) => (
                            <div key={p.period} className="grid grid-cols-[70px_1fr_auto] items-center gap-3 text-xs">
                                <span className="text-gray-600">{fmtPeriod(p.period)}</span>
                                <div className="space-y-1">
                                    <div className="h-2.5 rounded-full bg-green-500" style={{ width: `${Math.max((p.collected / maxBar) * 100, p.collected > 0 ? 1 : 0)}%` }} />
                                    <div className="h-2.5 rounded-full bg-amber-400" style={{ width: `${Math.max((p.earned / maxBar) * 100, p.earned > 0 ? 1 : 0)}%` }} />
                                </div>
                                <span className="text-gray-500 text-right">{inr(p.collected)} / {inr(p.earned)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Period table */}
            <div className="bg-white rounded-xl shadow overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase text-gray-500 bg-gray-50">
                            <th className="px-4 py-3">{applied.groupBy === "day" ? "Day" : "Month"}</th>
                            <th className="px-4 py-3 text-right">Transactions</th>
                            <th className="px-4 py-3 text-right">Order Value</th>
                            <th className="px-4 py-3 text-right">Collected</th>
                            <th className="px-4 py-3 text-right">GoerOne cust. discount</th>
                            <th className="px-4 py-3 text-right">Earned</th>
                        </tr>
                    </thead>
                    <tbody>
                        {series.map((p) => (
                            <tr key={p.period} className="border-t hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium text-gray-800">{fmtPeriod(p.period)}</td>
                                <td className="px-4 py-3 text-right">{num(p.transactions)}</td>
                                <td className="px-4 py-3 text-right">{inr(p.order_value)}</td>
                                <td className="px-4 py-3 text-right text-green-600">{inr(p.collected)}</td>
                                <td className="px-4 py-3 text-right text-purple-600">{inr(p.goerone_discount)}</td>
                                <td className={`px-4 py-3 text-right ${diffCls(p.earned)}`}>{inr(p.earned)}</td>
                            </tr>
                        ))}
                    </tbody>
                    {series.length > 0 && (
                        <tfoot>
                            <tr className="border-t-2 bg-gray-50 font-bold">
                                <td className="px-4 py-3">Total</td>
                                <td className="px-4 py-3 text-right">{num(s.transactions)}</td>
                                <td className="px-4 py-3 text-right">{inr(s.order_value)}</td>
                                <td className="px-4 py-3 text-right">{inr(s.collected)}</td>
                                <td className="px-4 py-3 text-right">{inr(s.goerone_discount)}</td>
                                <td className="px-4 py-3 text-right">{inr(s.earned)}</td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {/* Transaction ledger */}
            <div className="bg-white rounded-xl shadow overflow-x-auto">
                <div className="px-4 py-3 border-b"><h4 className="font-semibold text-gray-800">Transactions</h4></div>
                <ErrorBanner message={ledger.error} />
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase text-gray-500 bg-gray-50">
                            <th className="px-4 py-3">Date</th>
                            <th className="px-4 py-3">Reference</th>
                            <th className="px-4 py-3">Customer</th>
                            <th className="px-4 py-3 text-right">Order</th>
                            <th className="px-4 py-3 text-right">Comission</th>
                            {/* <th className="px-4 py-3 text-right">Store disc.</th>
                            <th className="px-4 py-3 text-right">Customer disc.</th> */}
                            <th className="px-4 py-3 text-right">GoerOne cust. disc.</th>
                            <th className="px-4 py-3 text-right">Earned</th>
                            {/* <th className="px-4 py-3">Settlement</th> */}
                        </tr>
                    </thead>
                    <tbody>
                        {ledger.loading && <tr><td colSpan={10}><ShimmerLoader /><ShimmerLoader /><ShimmerLoader /></td></tr>}
                        {!ledger.loading && rows.length === 0 && <tr><td colSpan={10} className="text-center text-gray-500 py-10">No transactions found.</td></tr>}
                        {!ledger.loading && rows.map((r) => (
                            <tr key={r.redemption_id} className="border-t hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap">{fmtDateTime(r.redemption_date)}</td>
                                <td className="px-4 py-3">{r.referneceNo || "—"}</td>
                                <td className="px-4 py-3">{customerName(r)}</td>
                                <td className="px-4 py-3 text-right">{inr(r.order_amount)}</td>
                                <td className="px-4 py-3 text-right">{inr(r.commission_collected)}</td>
                                {/* <td className="px-4 py-3 text-right">{inr(r.store_discount)}</td>
                                <td className="px-4 py-3 text-right">{inr(r.discountCust)}</td> */}
                                <td className="px-4 py-3 text-right">{inr(r.customer_discount_by_goerone)}</td>
                                <td className={`px-4 py-3 text-right font-medium ${diffCls(r.commission_earned)}`}>{inr(r.commission_earned)}</td>
                                {/* <td className="px-4 py-3">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.settlement_status === "settled" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                                        {r.settlement_status === "settled" ? "Settled" : "Pending"}
                                    </span>
                                </td> */}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {lastPage > 1 && <div className="px-4 py-3 border-t"><Pagination totalPages={lastPage} currentPage={page} setCurrentPage={setPage} /></div>}
            </div>
        </div>
    );
}